import {
  Button,
  Menu,
  MenuItem,
  pxToRem,
  ResizeHandler,
  SidenavService,
  SidenavToggleButton,
} from '@abb-procure/common';
import { SMALL_SCREEN_WIDTH_THRESHOLD } from '@abb-procure/constants';
import { PreferencesState, UserState } from '@abb-procure/data';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { RouterOutlet } from '@angular/router';
import { AppSecondaryNavigation } from '../app-secondary-navigation/app-secondary-navigation.component';
import { CreateMenu } from '../create-menu/create-menu.component';

@Component({
  // Existing usage is attribute-based in the shell layout; changing this would break templates.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[procure-app-navigation]',
  templateUrl: './app-navigation.component.html',
  styleUrl: './app-navigation.component.scss',
  imports: [
    Button,
    Menu,
    MenuItem,
    RouterOutlet,
    CreateMenu,
    MatMenuTrigger,
    AppSecondaryNavigation,
    ResizeHandler,
    SidenavToggleButton,
  ],
  providers: [SidenavService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppNavigation {
  readonly primaryNavElement = viewChild('primaryNav', { read: ElementRef });

  protected readonly preferences = inject(PreferencesState);
  protected readonly userState = inject(UserState);
  protected readonly sidenavService = inject(SidenavService);

  protected readonly hasAsideContent = signal(false);

  protected resizingEvent = {
    isResizing: false,
    startingCursorX: 0,
    startingWidth: 0,
  };

  constructor() {
    this.sidenavService.setSideNavOptions({
      cssVarName: '--procure-navigation-width',
      placement: 'left',
      clampedMinWidth: 14,
      clampedMaxWidth: 30,
      defaultWidth: 18,
    });

    effect(() => {
      const preferredWidth = this.preferences.navigationWidth();
      if (preferredWidth) {
        this.sidenavService.setSidenavWidth(preferredWidth);
      } else {
        this.sidenavService.setBaseWidth();
      }
    });

    effect(() => {
      if (
        this.preferences.isNavigationExpanded() !==
        this.sidenavService.isExpanded()
      ) {
        this.preferences.setNavigationWidth(this.sidenavService.width());
      }
    });
  }

  @HostListener('window:mousemove', ['$event'])
  updateSidenavWidth(event: MouseEvent): void {
    if (!this.resizingEvent.isResizing) {
      return;
    }
    this.sidenavService.resizeSidenav(
      this.resizingEvent.startingCursorX,
      event.clientX,
      this.resizingEvent.startingWidth,
    );
  }

  @HostListener('window:mouseup')
  stopResizing(): void {
    if (this.resizingEvent.isResizing) {
      this.preferences.setNavigationWidth(this.sidenavService.width());
    }
    this.resizingEvent.isResizing = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const width = window.innerWidth;
    if (
      width <= SMALL_SCREEN_WIDTH_THRESHOLD &&
      this.sidenavService.isExpanded()
    ) {
      this.toggle();
    }
  }

  protected startResizing(event: MouseEvent): void {
    const currentHostElementWidth =
      this.primaryNavElement()?.nativeElement.offsetWidth;

    this.resizingEvent = {
      isResizing: true,
      startingCursorX: pxToRem(event.clientX),
      startingWidth: pxToRem(currentHostElementWidth),
    };
  }

  protected toggle(): void {
    this.sidenavService.toggleExpansion();
    this.preferences.setNavigationWidth(this.sidenavService.width());
  }

  protected collapsePrimary(): void {
    if (this.sidenavService.isExpanded()) {
      this.sidenavService.setCollapsedWidth();
      this.preferences.setNavigationWidth(this.sidenavService.width());
    }
  }
}
